import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import AssessmentIcon from '@material-ui/icons/Assessment';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import Layout from '../components/Layout/Layout';
import {
  hasDirectionalProbabilities,
  normalizeTrendAnalyserInput,
} from '../utils/trendAnalyserAdapter';

const useStyles = makeStyles((theme) => ({
  intro: {
    marginBottom: theme.spacing(2),
    borderLeft: '4px solid #f47d06',
  },
  icon: {
    color: '#f47d06',
    marginRight: theme.spacing(1.5),
  },
  tabs: {
    borderBottom: '1px solid #e0e0e0',
  },
  section: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
  },
  card: {
    height: '100%',
  },
  label: {
    color: '#777',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  value: {
    marginTop: theme.spacing(0.75),
    fontWeight: 600,
  },
  detail: {
    marginTop: theme.spacing(0.5),
    color: '#777',
    fontSize: 12,
  },
  empty: {
    minHeight: 280,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  emptyIcon: {
    color: '#aaa',
    fontSize: 52,
    marginBottom: theme.spacing(1.5),
  },
  probability: {
    marginTop: theme.spacing(1.5),
  },
  qualityChip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  hiddenInput: {
    display: 'none',
  },
}));

const displayNumber = (value, options = {}) => (
  value === null
    ? 'Not available'
    : new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2, ...options }).format(value)
);

const MetricCard = ({ label, value, detail }) => {
  const classes = useStyles();
  return (
    <Card className={classes.card} variant="outlined">
      <CardContent>
        <Typography className={classes.label}>{label}</Typography>
        <Typography variant="h6" className={classes.value}>{value}</Typography>
        {detail && <Typography className={classes.detail}>{detail}</Typography>}
      </CardContent>
    </Card>
  );
};

const Probability = ({ label, value, color }) => {
  const classes = useStyles();
  const resolvedValue = value === null ? 0 : Math.min(Math.max(value, 0), 100);
  return (
    <Box className={classes.probability}>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2">{value === null ? 'Not available' : `${displayNumber(value)}%`}</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={resolvedValue}
        style={{ height: 8, borderRadius: 4, marginTop: 6, backgroundColor: '#eee' }}
        color={color}
      />
    </Box>
  );
};

function MarketIntelligence() {
  const classes = useStyles();
  const fileInput = useRef(null);
  const [instrument, setInstrument] = useState(0);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const isNifty = instrument === 0;

  const importReport = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;

    setError('');
    try {
      const payload = JSON.parse(await file.text());
      setReport(normalizeTrendAnalyserInput(payload));
      setInstrument(0);
    } catch (importError) {
      setReport(null);
      setError(importError instanceof SyntaxError
        ? 'The selected file is not valid JSON.'
        : importError.message);
    }
  };

  const market = report ? report.market : null;
  const giftGap = market && market.giftNifty !== null && market.niftyClose !== null
    ? market.giftNifty - market.niftyClose
    : null;

  return (
    <Layout title="Market Intelligence">
      <Paper className={classes.intro}>
        <Box p={2.5} display="flex" alignItems="center" flexWrap="wrap">
          <AssessmentIcon className={classes.icon} fontSize="large" />
          <Box flexGrow={1}>
            <Typography variant="h6">Pre-market intelligence</Typography>
            <Typography variant="body2" color="textSecondary">
              Review trend inputs without connecting a separate runtime service.
            </Typography>
          </Box>
          <input
            ref={fileInput}
            className={classes.hiddenInput}
            type="file"
            accept="application/json,.json"
            onChange={importReport}
          />
          {report && (
            <Button
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setReport(null)}
              style={{ marginRight: 8 }}
            >
              Clear
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInput.current.click()}
          >
            Import trend JSON
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}

      <Box mb={2}>
        <Alert severity="info">
          <AlertTitle>Read-only legacy input</AlertTitle>
          The import accepts the JSON produced by <code>trend_analyser</code>. Values are
          displayed for research only, stay in this browser tab, and never trigger orders.
        </Alert>
      </Box>

      <Paper>
        <Tabs
          value={instrument}
          onChange={(event, value) => setInstrument(value)}
          indicatorColor="secondary"
          textColor="primary"
          className={classes.tabs}
        >
          <Tab label="NIFTY 50" />
          <Tab label="BANKNIFTY" />
        </Tabs>

        {(!report || !isNifty) && (
          <Box className={classes.empty}>
            <Box maxWidth={560}>
              <AssessmentIcon className={classes.emptyIcon} />
              <Typography variant="h6">
                {!isNifty ? 'No BANKNIFTY input available' : 'Import a Trend Analyser report'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {!isNifty
                  ? 'The current Trend Analyser contract covers NIFTY 50 only.'
                  : 'Select its JSON output to inspect market pulse, technical levels, positioning, flows, global cues, and quality gaps.'}
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>

      {report && isNifty && (
        <>
          <Paper className={classes.section}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
              <Box>
                <Typography variant="h6">{report.instrument || 'NIFTY 50'} input snapshot</Typography>
                <Typography variant="body2" color="textSecondary">
                  Trading date: {report.tradingDate || 'Not available'} · Observation: {report.observationTime || 'Not available'}
                </Typography>
              </Box>
              <Chip label="Unverified input" color="secondary" variant="outlined" />
            </Box>
          </Paper>

          <Typography variant="h6" style={{ marginTop: 20, marginBottom: 8 }}>Market pulse</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}><MetricCard label="NIFTY close" value={displayNumber(market.niftyClose)} /></Grid>
            <Grid item xs={12} sm={6} md={3}><MetricCard label="GIFT Nifty" value={displayNumber(market.giftNifty)} detail={`Gap: ${giftGap === null ? 'Not available' : displayNumber(giftGap, { signDisplay: 'always' })}`} /></Grid>
            <Grid item xs={12} sm={6} md={3}><MetricCard label="India VIX" value={displayNumber(market.vix)} /></Grid>
            <Grid item xs={12} sm={6} md={3}><MetricCard label="RSI (14)" value={displayNumber(market.rsi)} /></Grid>
          </Grid>

          <Typography variant="h6" style={{ marginTop: 20, marginBottom: 8 }}>Levels and positioning</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}><MetricCard label="Support" value={displayNumber(market.support)} /></Grid>
            <Grid item xs={12} sm={6} md={3}><MetricCard label="Resistance" value={displayNumber(market.resistance)} /></Grid>
            <Grid item xs={12} sm={6} md={3}><MetricCard label="Highest call OI" value={displayNumber(market.callStrike)} detail={market.callOi === null ? 'OI not available' : `OI: ${displayNumber(market.callOi)}`} /></Grid>
            <Grid item xs={12} sm={6} md={3}><MetricCard label="Highest put OI" value={displayNumber(market.putStrike)} detail={market.putOi === null ? 'OI not available' : `OI: ${displayNumber(market.putOi)}`} /></Grid>
            <Grid item xs={12} sm={6} md={3}><MetricCard label="Put / Call ratio" value={displayNumber(market.pcr)} /></Grid>
          </Grid>

          <Typography variant="h6" style={{ marginTop: 20, marginBottom: 8 }}>Flows and global context</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}><MetricCard label="FII net (₹ Cr)" value={displayNumber(market.fiiNet, { signDisplay: 'exceptZero' })} /></Grid>
            <Grid item xs={12} sm={6} md={3}><MetricCard label="DII net (₹ Cr)" value={displayNumber(market.diiNet, { signDisplay: 'exceptZero' })} /></Grid>
            <Grid item xs={12} sm={6} md={3}><MetricCard label="S&P 500 change" value={market.sp500Change === null ? 'Not available' : `${displayNumber(market.sp500Change, { signDisplay: 'exceptZero' })}%`} /></Grid>
            <Grid item xs={12} sm={6} md={3}><MetricCard label="USD / INR" value={displayNumber(market.usdInr)} detail={`Sentiment: ${market.sentiment || 'Not available'}`} /></Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper className={classes.section}>
                <Typography variant="h6">Directional outlook</Typography>
                {!hasDirectionalProbabilities(report) && (
                  <Alert severity="warning" style={{ marginTop: 12 }}>
                    No directional probabilities were supplied. The analyser module is absent from the current legacy project.
                  </Alert>
                )}
                <Probability label="Bullish" value={report.probabilities.bullish} color="primary" />
                <Probability label="Sideways" value={report.probabilities.sideways} color="primary" />
                <Probability label="Bearish" value={report.probabilities.bearish} color="secondary" />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper className={classes.section}>
                <Typography variant="h6">Data quality and evidence</Typography>
                <Box mt={1.5}>
                  {report.qualityFlags.map((flag) => (
                    <Chip key={flag} label={flag.replace(/_/g, ' ')} size="small" className={classes.qualityChip} />
                  ))}
                </Box>
                <Typography variant="body2" color="textSecondary">
                  Source references: {report.sourceReferences.length > 0 ? report.sourceReferences.join(', ') : 'Not supplied'}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  Imported values remain unverified until the dashboard receives provider, observation time, ingestion time, units, freshness, and schema metadata.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Layout>
  );
}

export default MarketIntelligence;
